/**
 * CatalogueDemandPanel — Owner-only "what vendors are people asking for?"
 *
 * Surfaces the top-N most requested connectors from the public catalogue
 * watcher form on `/catalogue`. Turns the lead pipeline into a
 * product-roadmap voting tool at zero marginal cost.
 *
 * Calls GET /api/owner/catalogue-demand. Empty state hides the panel
 * entirely so the Tenants page isn't cluttered with "no data yet" tiles.
 */
import { useEffect, useState } from "react";
import { Flame, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export function CatalogueDemandPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/owner/catalogue-demand")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load demand"))
      .finally(() => setLoading(false));
  }, []);

  // Hide entirely until at least one detected request exists.
  if (loading) {
    return (
      <section
        className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
        data-testid="catalogue-demand-panel"
      >
        <p className="inline-flex items-center gap-2 text-sm text-ek-ink/60">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading vendor demand…
        </p>
      </section>
    );
  }
  if (err) {
    return (
      <section
        className="mt-6 rounded-2xl border border-red-200 bg-red-50/50 p-6"
        data-testid="catalogue-demand-panel"
      >
        <p className="text-sm text-red-700">{err}</p>
      </section>
    );
  }
  if (!data || !data.items?.length) {
    return null;
  }

  const max = Math.max(...data.items.map((i) => i.count));

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="catalogue-demand-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-ek-ink flex items-center gap-2">
            <Flame className="h-4 w-4 text-amber-500" /> Vendor demand —
            top connectors prospects asked for
          </h2>
          <p className="mt-1 text-sm text-ek-ink/70">
            Aggregated from the public catalogue watcher form. Each row is a
            connector multiple prospects have asked to see — a product-roadmap
            voting signal, free.
          </p>
        </div>
        <div className="text-right text-xs text-ek-ink/55">
          <p>
            <span className="font-semibold text-ek-ink">{data.total_watchers}</span> watchers
          </p>
          {data.unmatched > 0 && (
            <p title="Lead messages where no vendor name was detected">
              <span className="font-semibold text-ek-ink/70">{data.unmatched}</span> unmatched
            </p>
          )}
        </div>
      </div>
      <ul className="mt-4 space-y-2" data-testid="catalogue-demand-list">
        {data.items.map((item) => {
          const widthPct = max > 0 ? Math.round((item.count / max) * 100) : 0;
          return (
            <li
              key={item.code}
              className="rounded-lg border border-black/5 bg-ek-ink/[0.02] p-3"
              data-testid={`catalogue-demand-row-${item.code}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-ek-ink">{item.name}</p>
                  {item.is_premium && (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                      Premium
                    </span>
                  )}
                  {item.category && (
                    <span className="text-[11px] text-ek-ink/55">· {item.category}</span>
                  )}
                </div>
                <span
                  className="text-sm font-mono font-semibold text-ek-purple"
                  data-testid={`catalogue-demand-count-${item.code}`}
                >
                  {item.count}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-ek-ink/5">
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-ek-purple to-amber-400"
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default CatalogueDemandPanel;
